import React, { useState, useEffect } from "react";
import { T } from "../lib/theme";

/* ─── Dealer Response ─────────────────────────────────────
   Reached only via ?deal=<token>, a link a household generated in their
   own account and sent from their own email client. No session, no
   account, no app shell — the person reading this has no relationship
   with HomeFleet beyond holding the link.

   Two API calls, both public:
     GET  /api/deal-link?token=...   what the household sent
     POST /api/deal-response       the dealer's one reply

   ── The contact split ──
   This page shows a vehicle and a set of numbers. It does not show the
   household's name, email, phone or zip, because the API does not send
   them: functions/api/deal-link.js only reads household identity when
   the link's purpose is "confirmation" AND a household_confirmed
   revision exists on the deal. That check lives server-side on purpose.
   This component renders `contact` when it is present and has no ability
   to obtain it when it is not — there is nothing here to bypass, and a
   change to this file cannot widen what a dealer can see.

   The offer view says so out loud rather than leaving a dealer to wonder
   why there is no phone number. A dealer who cannot reach the buyer and
   does not know why assumes the lead is junk; one who is told contact
   details follow agreement understands the sequence.

   ── Dealer-supplied text ──
   Everything typed here is stored and later shown to the household and
   to Clyde. React escapes on render, and the server caps every field.
   Dates are collected through <input type="date"> so `expiresOn` is a
   real YYYY-MM-DD at the point of entry rather than 40 arbitrary
   characters the household's side has to parse defensively later. */

const MAX_INCENTIVES = 20;

export function DealerResponseScreen({ token }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");

  // null until the dealer picks one of the three actions.
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState("");
  const [offer, setOffer] = useState("");
  const [tradeAllowance, setTradeAllowance] = useState("");
  const [rate, setRate] = useState("");
  const [termMonths, setTermMonths] = useState("");
  const [stockNumber, setStockNumber] = useState("");
  const [vehicleAvailable, setVehicleAvailable] = useState(true);
  const [incentives, setIncentives] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/deal-link?token=${encodeURIComponent(token || "")}`)
      .then(async (res) => ({ ok: res.ok, body: await res.json().catch(() => ({})) }))
      .then(({ ok, body }) => {
        if (cancelled) return;
        if (!ok) { setLoadError(body.error || "This link is no longer valid."); setLoading(false); return; }
        setData(body);
        const o = body.offer || {};
        // The counter form opens pre-filled with what was actually sent,
        // so a dealer changing one number does not have to retype the
        // other four — and so an untouched field means "unchanged" rather
        // than "cleared", which is what the household will read it as.
        setOffer(o.offer != null ? String(o.offer) : "");
        setTradeAllowance(o.trade && o.trade.allowance != null ? String(o.trade.allowance) : "");
        setRate(o.rate != null ? String(o.rate) : "");
        setTermMonths(o.termMonths != null ? String(o.termMonths) : "");
        // rateBased is decided ONCE, from what was actually sent, and then
        // fixed. An incentive expressed as a subvented APR is not a dollar
        // amount and must not be editable as one -- offering a "$" field
        // for a 3.9%/60mo program invites a dealer to correct it into a
        // cash incentive that nobody offered. The kind of thing the
        // incentive IS comes from the offer; only its values are the
        // dealer's to change.
        setIncentives((o.incentives || []).slice(0, MAX_INCENTIVES).map((i) => ({
          label: i.label || "Incentive",
          amount: i.amount != null ? String(i.amount) : "",
          rate: i.rate != null ? String(i.rate) : "",
          termMonths: i.termMonths != null ? String(i.termMonths) : "",
          rateBased: i.amount == null && i.rate != null,
          source: i.source,
          applies: true,
          expiresOn: "",
          added: false,
        })));
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setLoadError("We could not load this offer. Check your connection and try again."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (kind) => {
    setErr("");
    if (kind === "dealer_declined" && !note.trim()) {
      setErr("Please add a short reason so the buyer knows where they stand.");
      return;
    }
    setBusy(true);
    try {
      // Only the incentives the dealer says apply are sent. An incentive
      // they unticked is simply absent, and the household can see exactly
      // which ones vanished because the offer_sent revision it was
      // compared against is still there, intact and unedited. That is
      // what storing full snapshots instead of diffs buys.
      const payload = {
        token,
        kind,
        note: note.trim() || null,
        response: {
          offer: kind === "dealer_counter" ? numOrNull(offer) : null,
          tradeAllowance: kind === "dealer_counter" ? numOrNull(tradeAllowance) : null,
          rate: kind === "dealer_counter" ? numOrNull(rate) : null,
          termMonths: kind === "dealer_counter" ? numOrNull(termMonths) : null,
          incentives: kind === "dealer_declined" ? [] : incentives
            // An added row left blank is a dealer who started typing and
            // changed their mind, not an incentive. Storing it would put an
            // unnamed line item in a permanent record the household reads.
            .filter((i) => i.applies && !(i.added && !i.label.trim() && !numOrNull(i.amount)))
            .slice(0, MAX_INCENTIVES)
            .map((i) => ({
              label: i.label.trim() || "Incentive",
              amount: numOrNull(i.amount),
              rate: numOrNull(i.rate),
              termMonths: numOrNull(i.termMonths),
              confirmed: true,
              expiresOn: i.expiresOn || null,
            })),
          vehicleAvailable,
          stockNumber: stockNumber.trim() || null,
        },
      };
      const res = await fetch("/api/deal-response", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setDone(kind);
      } else {
        // The server's message is shown as written. It distinguishes
        // "already answered" (409, retrying cannot help) from a transient
        // failure, and a dealer with no account has no other way to find
        // out which of those happened.
        setErr(body.error || "We could not record your response. Please contact the buyer directly.");
      }
    } catch {
      setErr("Connection error — your response was not recorded. Please try again.");
    }
    setBusy(false);
  };

  if (loading) return <Shell><Muted>Loading this offer...</Muted></Shell>;

  if (loadError) {
    return (
      <Shell>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 8 }}>This link isn't valid</div>
        <div style={{ fontSize: 13, color: T.textMd, lineHeight: 1.55 }}>{loadError}</div>
      </Shell>
    );
  }

  if (done) {
    const msg = done === "dealer_approved"
      ? "Your approval has been recorded and the buyer has been notified in their HomeFleet account."
      : done === "dealer_declined"
        ? "Your response has been recorded. The buyer will see your reason."
        : "Your counter-offer has been recorded. The buyer will see exactly what you changed.";
    return (
      <Shell>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.green, marginBottom: 8 }}>Response recorded</div>
        <div style={{ fontSize: 13, color: T.textMd, lineHeight: 1.6, marginBottom: 14 }}>{msg}</div>
        <div style={{ fontSize: 12, color: T.textSm, lineHeight: 1.6 }}>
          This link has now been used and won't open again. If you need to change something, contact the buyer directly.
        </div>
        <PreferredDealerCta />
      </Shell>
    );
  }

  const o = (data && data.offer) || {};
  const contact = data && data.contact;

  return (
    <Shell wide>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: T.teal, marginBottom: 6 }}>
        HomeFleet · Buyer Offer
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 4 }}>
        {o.vehicle || "Vehicle offer"}
      </div>
      <div style={{ fontSize: 12.5, color: T.textSm, marginBottom: 18 }}>
        {data.dealerLabel ? <>Sent to <strong style={{ color: T.textMd }}>{data.dealerLabel}</strong> · </> : null}
        {fmtDate(data.sentAt) ? `Sent ${fmtDate(data.sentAt)}` : "Recently sent"}
        {fmtDate(data.expiresAt) ? ` · Link expires ${fmtDate(data.expiresAt)}` : ""}
      </div>

      {data.note ? (
        <Card>
          <Label>A note from the buyer</Label>
          <div style={{ fontSize: 13.5, color: T.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{data.note}</div>
        </Card>
      ) : null}

      <Card>
        <Label>The offer</Label>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
          <div style={{ fontSize: 30, fontWeight: 800, color: T.teal }}>{money(o.offer) || "—"}</div>
          {o.askingPrice != null ? (
            <div style={{ fontSize: 13, color: T.textSm }}>
              against an asking price of <span style={{ color: T.textMd, fontWeight: 600 }}>{money(o.askingPrice)}</span>
            </div>
          ) : null}
        </div>
        <Rows rows={[
          ["VIN", o.vin],
          ["Mileage", o.mileage != null ? `${o.mileage.toLocaleString()} mi` : null],
          ["Discount applied", money(o.discount)],
          ["Net price", money(o.netPrice)],
        ]} />
      </Card>

      {o.trade ? (
        <Card>
          <Label>Trade-in</Label>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 8 }}>{o.trade.vehicle || "Trade vehicle"}</div>
          <Rows rows={[
            ["Mileage", o.trade.mileage != null ? `${o.trade.mileage.toLocaleString()} mi` : null],
            ["Allowance applied", money(o.trade.allowance)],
            ["Loan payoff", money(o.trade.payoff)],
            ["Net equity", money(o.trade.netEquity)],
            ["Valuation source", o.trade.source],
          ]} />
        </Card>
      ) : null}

      {(o.rate != null || o.termMonths != null || o.downPayment != null) ? (
        <Card>
          <Label>Financing assumed</Label>
          <Rows rows={[
            ["Rate", o.rate != null ? `${o.rate}%` : null],
            ["Term", o.termMonths != null ? `${o.termMonths} months` : null],
            ["Down payment", money(o.downPayment)],
          ]} />
        </Card>
      ) : null}

      {o.incentives && o.incentives.length ? (
        <Card>
          <Label>Incentives assumed</Label>
          <div style={{ fontSize: 12.5, color: T.textSm, marginBottom: 10, lineHeight: 1.55 }}>
            These came from public research, not from you. If any of them are wrong, correcting them below is the most
            useful thing on this page — you're the only one who actually knows.
          </div>
          {o.incentives.map((i, n) => (
            <div key={n} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderTop: n ? `1px solid ${T.border}` : "none" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: T.text }}>{i.label || "Incentive"}</div>
                {i.source ? <div style={{ fontSize: 11.5, color: T.textSm }}>{i.source}</div> : null}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.textMd, whiteSpace: "nowrap" }}>
                {money(i.amount) || (i.rate != null ? `${i.rate}%` : "—")}
              </div>
            </div>
          ))}
        </Card>
      ) : null}

      {/* The contact split, rendered. `contact` arrives only on a
          confirmation link for a deal the household has confirmed; on the
          offer view it is always null and the explanation takes its place. */}
      {contact ? (
        <Card accent={T.green}>
          <Label>Buyer contact</Label>
          <div style={{ fontSize: 12.5, color: T.textSm, marginBottom: 10, lineHeight: 1.55 }}>
            This deal has been confirmed by the buyer, so their contact details are now shared with you.
          </div>
          <Rows rows={[
            ["Name", contact.name],
            ["Email", contact.email],
            ["Phone", contact.phone],
            ["Zip", contact.zip],
          ]} />
        </Card>
      ) : (
        <Card>
          <Label>About the buyer</Label>
          <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6, marginBottom: 8 }}>
            A HomeFleet household is ready to proceed on this vehicle.
          </div>
          <div style={{ fontSize: 12.5, color: T.textSm, lineHeight: 1.6 }}>
            Their name and contact details aren't shown yet — they're shared once you and the buyer agree on terms.
            Everything you need to price this deal is above. Reply below and it goes straight to them.
          </div>
        </Card>
      )}

      {!mode ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 18 }}>
          <div style={{ fontSize: 12.5, color: T.textSm, marginBottom: 2 }}>You can reply once. Choose how you'd like to respond:</div>
          <Btn onClick={() => setMode("dealer_approved")} tone="green">Approve as-is</Btn>
          <Btn onClick={() => setMode("dealer_counter")} tone="teal">Counter or correct the details</Btn>
          <Btn onClick={() => setMode("dealer_declined")} tone="plain">Decline this offer</Btn>
        </div>
      ) : (
        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            onClick={() => { setMode(null); setErr(""); }}
            style={{ background: "none", border: "none", color: T.teal, fontSize: 12.5, fontWeight: 700, padding: 0, marginBottom: 12, cursor: "pointer" }}
          >
            ← Choose a different response
          </button>

          {mode === "dealer_counter" ? (
            <Card>
              <Label>Your counter</Label>
              <div style={{ fontSize: 12.5, color: T.textSm, marginBottom: 12, lineHeight: 1.55 }}>
                Pre-filled with what the buyer sent. Change only what's different — anything you leave alone is recorded as agreed.
              </div>
              <Field label="Your price" value={offer} onChange={setOffer} prefix="$" />
              {o.trade ? <Field label="Trade allowance you'd give" value={tradeAllowance} onChange={setTradeAllowance} prefix="$" /> : null}
              <Field label="Rate (APR)" value={rate} onChange={setRate} suffix="%" />
              <Field label="Term (months)" value={termMonths} onChange={setTermMonths} />
            </Card>
          ) : null}

          {mode !== "dealer_declined" && incentives.length ? (
            <Card>
              <Label>Which incentives actually apply?</Label>
              <div style={{ fontSize: 12.5, color: T.textSm, marginBottom: 10, lineHeight: 1.55 }}>
                Untick anything that doesn't apply, correct the amounts, and add an expiry date where you know one.
              </div>
              {incentives.map((i, n) => (
                <div key={n} style={{ padding: "10px 0", borderTop: n ? `1px solid ${T.border}` : "none" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: i.added ? "default" : "pointer", marginBottom: i.applies ? 8 : 0 }}>
                    <input
                      type="checkbox"
                      checked={i.applies}
                      onChange={(e) => setIncentives((prev) => prev.map((x, k) => (k === n ? { ...x, applies: e.target.checked } : x)))}
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    {i.added ? (
                      <input
                        value={i.label}
                        onChange={(e) => setIncentives((prev) => prev.map((x, k) => (k === n ? { ...x, label: e.target.value } : x)))}
                        maxLength={300}
                        autoFocus
                        placeholder="What is it called?"
                        style={{
                          flex: 1, minWidth: 0, border: `1.5px solid ${T.border}`, borderRadius: 8,
                          background: T.card, color: T.text, fontSize: 13, padding: "7px 10px",
                          fontFamily: "'Plus Jakarta Sans',sans-serif",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 13, color: i.applies ? T.text : T.textSm, textDecoration: i.applies ? "none" : "line-through" }}>
                        {i.label}
                      </span>
                    )}
                  </label>
                  {i.applies ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingLeft: 25 }}>
                      {i.rateBased ? (
                        <>
                          <Field
                            label="Rate"
                            value={i.rate}
                            onChange={(v) => setIncentives((prev) => prev.map((x, k) => (k === n ? { ...x, rate: v } : x)))}
                            suffix="%"
                            inline
                          />
                          <Field
                            label="Term (months)"
                            value={i.termMonths}
                            onChange={(v) => setIncentives((prev) => prev.map((x, k) => (k === n ? { ...x, termMonths: v } : x)))}
                            inline
                          />
                        </>
                      ) : (
                        <Field
                          label="Amount"
                          value={i.amount}
                          onChange={(v) => setIncentives((prev) => prev.map((x, k) => (k === n ? { ...x, amount: v } : x)))}
                          prefix="$"
                          inline
                        />
                      )}
                      <Field
                        label="Expires"
                        type="date"
                        value={i.expiresOn}
                        onChange={(v) => setIncentives((prev) => prev.map((x, k) => (k === n ? { ...x, expiresOn: v } : x)))}
                        inline
                      />
                    </div>
                  ) : null}
                </div>
              ))}
              {incentives.length < MAX_INCENTIVES ? (
                <button
                  type="button"
                  onClick={() => setIncentives((prev) => [...prev, { label: "", amount: "", rate: "", termMonths: "", rateBased: false, source: null, applies: true, expiresOn: "", added: true }])}
                  style={{ background: "none", border: `1px dashed ${T.borderMd}`, color: T.textMd, fontSize: 12.5, fontWeight: 600, padding: "8px 12px", borderRadius: 8, marginTop: 10, cursor: "pointer", width: "100%" }}
                >
                  + Add an incentive we missed
                </button>
              ) : null}
            </Card>
          ) : null}

          <Card>
            <Label>{mode === "dealer_declined" ? "Why are you declining?" : "Anything else?"}</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              rows={4}
              autoFocus
              placeholder={
                mode === "dealer_declined"
                  ? "Required — e.g. the vehicle sold, or the price is below our floor."
                  : "Optional — anything the buyer should know."
              }
              style={{
                width: "100%", boxSizing: "border-box", padding: "10px 12px", fontSize: 13.5,
                border: `1.5px solid ${T.border}`, borderRadius: 8, background: T.cardAlt, color: T.text,
                fontFamily: "'Plus Jakarta Sans',sans-serif", resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12, alignItems: "flex-end" }}>
              <Field label="Stock number" value={stockNumber} onChange={setStockNumber} inline />
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: T.textMd, paddingBottom: 9 }}>
                <input type="checkbox" checked={vehicleAvailable} onChange={(e) => setVehicleAvailable(e.target.checked)} style={{ width: 16, height: 16 }} />
                This vehicle is still available
              </label>
            </div>
          </Card>

          {err ? (
            <div style={{ fontSize: 13, color: T.red, background: T.redL, border: `1px solid ${T.redBorderStrong}`, borderRadius: 8, padding: "10px 12px", marginBottom: 12, lineHeight: 1.5 }}>
              {err}
            </div>
          ) : null}

          <Btn onClick={() => submit(mode)} disabled={busy} tone={mode === "dealer_declined" ? "plain" : mode === "dealer_approved" ? "green" : "teal"}>
            {busy ? "Sending..." : mode === "dealer_approved" ? "Approve this offer" : mode === "dealer_declined" ? "Send decline" : "Send counter"}
          </Btn>
          <div style={{ fontSize: 11.5, color: T.textSm, textAlign: "center", marginTop: 9, lineHeight: 1.5 }}>
            You can only reply once — this link closes after you send.
          </div>
        </div>
      )}

      <PreferredDealerCta />
    </Shell>
  );
}

/* Below the response, never competing with it. A dealer who answers the
   offer and ignores this has done the important thing. */
function PreferredDealerCta() {
  return (
    <div style={{ marginTop: 26, paddingTop: 18, borderTop: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.textMd, marginBottom: 6 }}>Become a preferred dealer</div>
      <div style={{ fontSize: 12.5, color: T.textSm, lineHeight: 1.6 }}>
        HomeFleet households send offers to dealers they've researched. Preferred dealers get these offers first.{" "}
        <a href="mailto:hello@cradlehq.app?subject=Preferred%20dealer%20enquiry" style={{ color: T.teal, fontWeight: 700, textDecoration: "none" }}>
          hello@cradlehq.app
        </a>
      </div>
    </div>
  );
}

function Shell({ children, wide }) {
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Plus Jakarta Sans',sans-serif", padding: "28px 16px" }}>
      <div style={{ maxWidth: wide ? 560 : 400, margin: "0 auto", background: T.card, borderRadius: 16, padding: "26px 22px", boxShadow: T.shadow }}>
        {children}
      </div>
    </div>
  );
}

function Card({ children, accent }) {
  return (
    <div style={{
      background: T.cardAlt, border: `1px solid ${accent || T.border}`, borderRadius: 12,
      padding: "14px 16px", marginBottom: 12,
    }}>{children}</div>
  );
}

function Label({ children }) {
  return (
    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: T.textSm, marginBottom: 8 }}>
      {children}
    </div>
  );
}

function Muted({ children }) {
  return <div style={{ fontSize: 13, color: T.textMuted }}>{children}</div>;
}

/* Rows whose value is null are dropped entirely rather than rendered as
   "—". An empty field on a document a dealer is pricing from invites them
   to read it as a zero; showing nothing says nothing. */
function Rows({ rows }) {
  const visible = rows.filter((r) => r[1] != null && r[1] !== "");
  if (!visible.length) return null;
  return (
    <div>
      {visible.map(([k, v], n) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderTop: n ? `1px solid ${T.border}` : "none" }}>
          <span style={{ fontSize: 12.5, color: T.textSm }}>{k}</span>
          <span style={{ fontSize: 13, color: T.text, fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, prefix, suffix, type, inline }) {
  return (
    <div style={{ marginBottom: inline ? 0 : 10, flex: inline ? "1 1 130px" : undefined, minWidth: inline ? 130 : undefined }}>
      <div style={{ fontSize: 11.5, color: T.textSm, marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", border: `1.5px solid ${T.border}`, borderRadius: 8, background: T.card, padding: "0 10px" }}>
        {prefix ? <span style={{ fontSize: 13, color: T.textSm, marginRight: 2 }}>{prefix}</span> : null}
        <input
          type={type || "text"}
          inputMode={type === "date" ? undefined : "decimal"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
            color: T.text, fontSize: 13.5, padding: "9px 0", fontFamily: "'Plus Jakarta Sans',sans-serif",
          }}
        />
        {suffix ? <span style={{ fontSize: 13, color: T.textSm, marginLeft: 2 }}>{suffix}</span> : null}
      </div>
    </div>
  );
}

function Btn({ children, onClick, disabled, tone }) {
  const bg = tone === "green" ? `linear-gradient(135deg,${T.green},#16a34a)`
    : tone === "teal" ? `linear-gradient(135deg,${T.teal},#0f766e)`
      : T.slateL;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%", padding: "12px 0", background: bg,
        color: tone === "plain" ? T.textMd : "#06231f",
        border: tone === "plain" ? `1.5px solid ${T.border}` : "none",
        borderRadius: 10, fontWeight: 700, fontSize: 14,
        fontFamily: "'Plus Jakarta Sans',sans-serif",
        opacity: disabled ? .6 : 1, cursor: disabled ? "default" : "pointer",
      }}
    >{children}</button>
  );
}

function money(v) {
  if (v == null || !Number.isFinite(Number(v))) return null;
  return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// Absent stays absent. The strip-then-parse below is what lets a dealer
// type "$50,900" and have it understood, but stripping "about the same"
// leaves an empty string, and Number("") is 0 -- so typed prose became a
// zero-dollar counter. Checked after stripping, not just before.
function numOrNull(v) {
  if (v == null || String(v).trim() === "") return null;
  const cleaned = String(v).replace(/[^0-9.-]/g, "");
  if (cleaned.trim() === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/* Guarded, per the plan's open item on unvalidated dates. Both values this
   renders (sentAt, expiresAt) are ISO strings this system wrote, but the
   habit is the point: a date that will not parse renders as nothing rather
   than as "Invalid Date" on a page a stranger is reading. */
function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
